import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  BottomNavigation,
  Button,
  FormDirtyModal,
  PageModal,
  TextEditor,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';

type EditDescriptionModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: ({ description }: { description: string }) => void;
  defaultValues: {
    description: string;
  };
};

type Form = {
  description: string;
};

export const EditDescriptionModal = ({
  open,
  onClose,
  onSave,
  defaultValues,
}: EditDescriptionModalProps) => {
  const { t } = useTranslation();

  const [displayFormDirtyModal, setDisplayFormDirtyModal] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Form>({
    defaultValues,
  });

  const onSubmit = async (data: Form) => {
    onSave({ description: data.description });
    onClose();
  };

  const onCancel = () => {
    if (isDirty) {
      setDisplayFormDirtyModal(true);
    } else {
      onClose();
    }
  };

  const title = defaultValues.description
    ? t('resources.form.description-modal.edit-description')
    : t('resources.form.description-modal.add-description');

  return (
    <PageModal open={open} onClose={onCancel} title={title}>
      <div className="w-full min-w-small">
        <div className="mb-4">
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextEditor {...field} defaultValue={field.value} />
            )}
          />
        </div>
        <BottomNavigation>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
          >
            {t('common.save')}
          </Button>
          <Button variant="outlined" onClick={onCancel}>
            {t('common.close')}
          </Button>
        </BottomNavigation>
      </div>
      <FormDirtyModal
        open={displayFormDirtyModal}
        onClose={() => setDisplayFormDirtyModal(false)}
        onCloseWithoutSaving={onClose}
      />
    </PageModal>
  );
};
